import type { ApprovalGateKind, ApprovalItem } from './projections.ts';

/** Today is a compact ready-work preview, never a business-priority ranking. */
export const TODAY_READY_APPROVAL_LIMIT = 5;

export type ApprovalTriageGroupId = 'ready_for_you' | 'setup_needed' | 'with_someone_else';

export interface ApprovalTriageFilters {
  readonly titleSearch?: string;
  readonly kinds?: readonly ApprovalGateKind[];
  readonly mineOnly?: boolean;
}

export interface ApprovalTriageSummary {
  readonly total: number;
  readonly mine: number;
  readonly readyForYou: number;
  readonly setupNeeded: number;
  readonly withSomeoneElse: number;
}

export interface ApprovalTriageGroup {
  readonly id: ApprovalTriageGroupId;
  readonly label: string;
  readonly items: readonly ApprovalItem[];
}

export interface ApprovalTriageView {
  /** Counts always describe the complete source projection, never a filtered or truncated subset. */
  readonly summary: ApprovalTriageSummary;
  /** Filtered groups retain source order within the fixed authority-first group order. */
  readonly groups: readonly ApprovalTriageGroup[];
  readonly shown: number;
  /** The first five source-ordered Ready-for-you items; no age or business-priority inference. */
  readonly todayReady: readonly ApprovalItem[];
}

const GROUP_LABEL: Readonly<Record<ApprovalTriageGroupId, string>> = {
  ready_for_you: 'Ready for you',
  setup_needed: 'Setup needed',
  with_someone_else: 'With someone else',
};

/** Authority is decided before readiness; ambiguous values never widen actionability. */
export function classifyApproval(item: ApprovalItem): ApprovalTriageGroupId {
  if (item.boundToMe !== true) return 'with_someone_else';
  return item.readiness?.ready === true ? 'ready_for_you' : 'setup_needed';
}

/** The single actionability predicate consumed by grouping and every decision affordance. */
export function isApprovalReadyForViewer(item: ApprovalItem): boolean {
  return classifyApproval(item) === 'ready_for_you';
}

function grouped(items: readonly ApprovalItem[]): Record<ApprovalTriageGroupId, ApprovalItem[]> {
  const result: Record<ApprovalTriageGroupId, ApprovalItem[]> = {
    ready_for_you: [],
    setup_needed: [],
    with_someone_else: [],
  };
  for (const item of items) result[classifyApproval(item)].push(item);
  return result;
}

function summarise(items: readonly ApprovalItem[]): ApprovalTriageSummary {
  const groups = grouped(items);
  return {
    total: items.length,
    mine: groups.ready_for_you.length + groups.setup_needed.length,
    readyForYou: groups.ready_for_you.length,
    setupNeeded: groups.setup_needed.length,
    withSomeoneElse: groups.with_someone_else.length,
  };
}

/** Apply literal title search, exact-kind OR, then the optional Mine secondary filter. */
function filteredItems(
  items: readonly ApprovalItem[],
  filters: ApprovalTriageFilters,
): readonly ApprovalItem[] {
  const titleSearch = (filters.titleSearch ?? '').trim().toLowerCase();
  const kinds = new Set(filters.kinds ?? []);
  return items.filter((item) => {
    if (titleSearch !== '' && !item.title.toLowerCase().includes(titleSearch)) return false;
    if (kinds.size > 0 && !kinds.has(item.kind)) return false;
    return filters.mineOnly !== true || item.boundToMe === true;
  });
}

/** Build the one client-side Approvals/Today triage view from the actor-scoped server projection. */
export function createApprovalTriage(
  items: readonly ApprovalItem[],
  filters: ApprovalTriageFilters = {},
): ApprovalTriageView {
  const allGroups = grouped(items);
  const visible = filteredItems(items, filters);
  const visibleGroups = grouped(visible);
  const groups: readonly ApprovalTriageGroup[] = [
    {
      id: 'ready_for_you',
      label: GROUP_LABEL.ready_for_you,
      items: visibleGroups.ready_for_you,
    },
    { id: 'setup_needed', label: GROUP_LABEL.setup_needed, items: visibleGroups.setup_needed },
    {
      id: 'with_someone_else',
      label: GROUP_LABEL.with_someone_else,
      items: visibleGroups.with_someone_else,
    },
  ];
  return {
    summary: summarise(items),
    groups,
    shown: visible.length,
    todayReady: allGroups.ready_for_you.slice(0, TODAY_READY_APPROVAL_LIMIT),
  };
}
